import { useState } from 'react';
import { Icon } from '../components/ui/Icon';
import { useT } from '../i18n';
import { ShareDialog } from './ShareDialog';
import type { ShareAdapter, ShareSubject } from './types';

interface Props {
  subject: ShareSubject;
  adapter: ShareAdapter;
  /**
   * `header` sits on an accent-colored bar, `surface` on a card.
   * Same two variants TMS had (`header` / `tarjeta`).
   */
  variant?: 'header' | 'surface';
  /**
   * Whether the current user may share this subject at all. Hides the button.
   * This is presentation only — the real gate is RLS.
   */
  canShare?: boolean;
  /** Use a text label when a compact layout needs a clearly named action. */
  showLabelOnMobile?: boolean;
  className?: string;
}

const STYLES: Record<'header' | 'surface', string> = {
  header: 'bg-accent text-accent-fg hover:opacity-90',
  surface: 'bg-accent text-accent-fg hover:opacity-90',
};

/** "Add person" icon that opens the share dialog. Port of TMS `CompartirPagina`. */
export function ShareButton({
  subject,
  adapter,
  variant = 'header',
  canShare = true,
  showLabelOnMobile = false,
  className = '',
}: Props) {
  const t = useT();
  const [open, setOpen] = useState(false);

  if (!canShare) return null;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label={t('share.open', { name: subject.name })}
        title={t('share.share')}
        className={`flex h-8 items-center justify-center gap-1.5 rounded-full px-3 text-xs font-medium disabled:cursor-not-allowed disabled:opacity-60 ${STYLES[variant]} ${className}`}
      >
        <Icon name="userPlus" className="h-3.5 w-3.5" />
        <span className={showLabelOnMobile ? undefined : 'hidden sm:inline'}>{t('share.share')}</span>
      </button>
      {open && (
        <ShareDialog
          subject={subject}
          adapter={adapter}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}
